import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
	QuestionnaireResult,
	useUpdateQuestionnaireResultScoreMutation,
} from '../../../../../graphql/graphql';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import DataTable, {
	DataTableColumn,
} from '../../../../Base/Components/DataTable/DataTable';
import QuestionnaireResultWithScoringDetailValueCell from './QuestionnaireResultWithScoringDetailValueCell';
import QuestionnaireResultWithScoringDetailScorePicker from '../QuestionnaireResultWithScoringDetailScorePicker/QuestionnaireResultWithScoringDetailScorePicker';
import {
	FormValues,
	getQuestionnaireScoringRows,
	getScoreDefaultValues,
	getScoredItemsInput,
	QuestionnaireScoringRow,
} from '../Helpers/QuestionnaireResultWithScoringDetail.helper';

import styles from './QuestionnaireResultWithScoringDetail.module.scss';

interface Props {
	item: QuestionnaireResult;
	onSubmit: () => void;
}

const QuestionnaireResultWithScoringDetail: React.FC<Props> = ({
	item,
	onSubmit,
}) => {
	const [updateScore] = useUpdateQuestionnaireResultScoreMutation();

	const formMethods = useForm<FormValues>({
		defaultValues: {
			scores: {},
		},
	});

	const rows = useMemo<QuestionnaireScoringRow[]>(() => {
		return getQuestionnaireScoringRows(
			item?.questionnaireResultItems || [],
			item.questionnaire?.code,
		);
	}, [item.questionnaire?.code]);

	useEffect(() => {
		formMethods.reset(getScoreDefaultValues(rows));
	}, [rows, formMethods]);

	const columns = useMemo<
		DataTableColumn<QuestionnaireScoringRow, undefined>[]
	>(
		() => [
			{
				key: 'question',
				label: 'Otázka',
				visible: true,
				renderItem: row => row.questionText,
			},
			{
				key: 'answer',
				label: 'Odpověď',
				visible: true,
				renderItem: row => (
					<QuestionnaireResultWithScoringDetailValueCell
						values={row.values}
						patientId={item.userId!}
					/>
				),
			},
			{
				key: 'score',
				label: 'Body',
				visible: true,
				renderItem: row =>
					row.maxScore === null ? (
						<></>
					) : (
						<QuestionnaireResultWithScoringDetailScorePicker
							name={`scores.${row.id}`}
							maxScore={row.maxScore}
						/>
					),
			},
		],
		[item.userId],
	);

	const handleSave = async (values: FormValues) => {
		await updateScore({
			input: {
				questionnaireResultId: item.id,
				items: getScoredItemsInput(rows, values),
			},
		});

		onSubmit();
	};

	return (
		<div className={styles.wrapper}>
			<FormWrapper formMethods={formMethods}>
				<DataTable<QuestionnaireScoringRow, undefined>
					data={rows}
					table={undefined}
					columns={columns}
				/>

				<div className='mt-4'>
					<SubmitButton<FormValues>
						title='Uložit bodové hodnocení'
						onSubmit={handleSave}
					/>
				</div>
			</FormWrapper>
		</div>
	);
};

export default QuestionnaireResultWithScoringDetail;
